import { useMutation } from "@apollo/client";
import cx from "classnames";

import CodeEditor from "components/CodeEditor";
import FlashContext from "components/FlashMessages/FlashContext";
import Box from "ds/components/Box";
import Button from "ds/components/Button";
import PageInfoHeader from "ds/components/PageInfoHeader";
import Select from "ds/components/Select";
import Tooltip from "ds/components/Tooltip";
import useTypedContext from "hooks/useTypedContext";
import { trackSegmentEvent } from "shared/Analytics";
import { Policy, PolicyEngineType } from "types/generated";
import { AccessResult } from "utils/access";

import { UPDATE_POLICY_V2, UpdatePolicyData } from "../gql";
import styles from "./styles.module.css";

type PolicyDetailsBodyContentProps = {
  policy: Policy;
  body: string;
  handleEditorChange: (value?: string) => void;
  policyEngineType: PolicyEngineType;
  setPolicyEngineType: (value: PolicyEngineType) => void;
  isSimulationVisible: boolean;
  canManagePolicy: AccessResult;
  canSimulatePolicies: AccessResult;
};

const PolicyDetailsBodyContent = ({
  body,
  handleEditorChange,
  policyEngineType,
  setPolicyEngineType,
  isSimulationVisible,
  policy,
  canManagePolicy,
  canSimulatePolicies,
}: PolicyDetailsBodyContentProps) => {
  const { onError, reportSuccess } = useTypedContext(FlashContext);

  const [updatePolicyV2] = useMutation(UPDATE_POLICY_V2, {
    variables: {
      id: policy.id,
    },
    refetchQueries: ["GetPolicy", "GetPolicyForDetails"],
  });

  const handleSaveChanges = async () => {
    let data: UpdatePolicyData | null | undefined;
    try {
      data = await updatePolicyV2({
        variables: {
          input: {
            name: policy.name,
            space: policy.spaceDetails.id,
            labels: policy.labels,
            description: policy.description,
            body,
            engineType: policyEngineType,
          },
        },
        // eslint-disable-next-line @cspell/spellchecker
      }).then(({ data }) => data?.policyUpdatev2);
      if (data) {
        trackSegmentEvent("Policy Body Updated");
        reportSuccess({ message: "Policy successfully updated" });
      }
    } catch (error) {
      onError(error);
    }
  };

  return (
    <Box
      direction="column"
      className={cx(
        styles.bodyWrapper,
        !canSimulatePolicies.result && styles.withoutSimulationPanel,
        isSimulationVisible && styles.narrowed
      )}
    >
      <PageInfoHeader title="Policy body">
        <Select
          value={policyEngineType}
          isDisabled={!canManagePolicy.result}
          items={[
            { label: "Rego V0", value: PolicyEngineType.RegoV0 },
            { label: "Rego V1", value: PolicyEngineType.RegoV1 },
          ]}
          onChange={(value: PolicyEngineType) => setPolicyEngineType(value)}
        />
        {(canManagePolicy.result || canManagePolicy.message) && (
          <Tooltip
            active={!!canManagePolicy.message}
            on={(props) => (
              <Button
                {...props}
                variant="primary"
                disabled={!canManagePolicy.result}
                onPress={handleSaveChanges}
              >
                Save changes
              </Button>
            )}
          >
            {canManagePolicy.message}
          </Tooltip>
        )}
      </PageInfoHeader>
      <CodeEditor
        readOnly={!canManagePolicy.result}
        body={body}
        onChange={handleEditorChange}
        language="rego"
      />
    </Box>
  );
};

export default PolicyDetailsBodyContent;
